import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportClientError } from './reportClientError';
import { isChunkReloadPending, isStaleChunkError, reloadOnceForStaleChunk } from '../chunkReloadGuard';

/**
 * #2740 (KATHY): root error boundary — the backstop behind {@link installChunkReloadGuard}.
 *
 * Before this, ANY uncaught render error (the reproduced case: a stale post-deploy tab whose
 * lazy route chunk 404s) unmounted the whole React tree and left a silent blank page —
 * testers report it as "opens a new blank window". React only keeps siblings alive when an
 * error boundary exists; the app had none (verified: zero getDerivedStateFromError /
 * componentDidCatch in frontend/src as of slice-3314).
 *
 * The fallback is deliberately dependency-free (no router, no stores — they may be the thing
 * that crashed): a visible message + a Reload button.
 */
type Props = { children: ReactNode };
type State = { error: Error | null; reloading: boolean };

export class AppErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, reloading: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Crash lane 19 Oct (RUSS/ROGER/LINK1): a stale-chunk error after a deploy is recovered by a
    // reload — either the vite:preloadError guard already issued it (pending), or slice-3709's
    // boundary-side twin issues it now (LINK1's blank Test Screens). That is not a crash: tag the
    // report so the server log does not read as one, and show the calm note, not the red panel.
    // slice-4907 (RUSS, demo, 30 Oct): a preventDefault'ed preload error makes the dynamic import
    // resolve `undefined`, so a NAMED-export lazy route (`m.WarehouseStatusPage`) throws "reading
    // 'WarehouseStatusPage'", which no signature matches. ANY render error while this document's
    // reload is already in flight is the stale chunk, not a crash.
    if (
      isChunkReloadPending() ||
      (isStaleChunkError(error?.message) && reloadOnceForStaleChunk(error))
    ) {
      reportClientError({
        message: error.message || String(error),
        stack: error.stack,
        source: 'stale-chunk-reload',
      });
      this.setState({ reloading: true });
      return;
    }
    // Surface in the console for support screenshots / devtools triage.
    console.error('AppErrorBoundary caught:', error, info.componentStack);
    // slice-3616 (RUSS, demo): also ship it to the server log — a customer-facing box has no
    // testers hub and nobody has devtools open, so the console alone left nothing to diagnose.
    reportClientError({
      message: error.message || String(error),
      stack: error.stack,
      componentStack: info.componentStack ?? undefined,
      source: 'boundary',
    });
    // Anything else — including a stale-chunk failure inside the 30 s loop window (the reload
    // did not help) — stays on the message + Reload button below.
  }

  override render(): ReactNode {
    if (this.state.error === null) return this.props.children;
    if (
      this.state.reloading ||
      isChunkReloadPending()
    ) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
          <div role="status" className="text-center text-sm text-slate-600">
            <p>A new version of PH10 was installed — loading it…</p>
            {/* Backstop: if the browser kept the page (e.g. "Stay on page" for unsaved edits). */}
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-3 rounded-md border border-slate-300 bg-white px-3 py-1 text-xs text-slate-700 hover:bg-slate-100"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    const message = this.state.error.message || String(this.state.error);
    const stackHead = (this.state.error.stack ?? '')
      .split('\n')
      .slice(1, 6)
      .map((l) => l.trim())
      .join('\n');
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
        <div
          role="alert"
          className="w-full max-w-lg rounded-lg border border-red-200 bg-white p-6 shadow-sm"
        >
          <h1 className="text-lg font-semibold text-slate-900">Something went wrong</h1>
          <p className="mt-2 text-sm text-slate-600">
            The screen hit an error it could not recover from. Reloading usually fixes this
            (it also picks up freshly deployed code).
          </p>
          <p className="mt-2 break-words rounded bg-slate-50 p-2 font-mono text-xs text-slate-500">
            {message}
          </p>
          {/* slice-3616: the top of the stack, so a screenshot of this screen names the code path. */}
          {stackHead && (
            <pre className="mt-2 max-h-40 overflow-auto rounded bg-slate-50 p-2 font-mono text-[10px] leading-4 text-slate-400">
              {stackHead}
            </pre>
          )}
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-4 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
